import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import createPaginationControl from "../../Common/createPaginationControl";
import LoadButton from "../../Common/LoadButton";
import Spinner from "../../Common/Spinner";
import NavigationBarContainer from "../NavigationBarContainer";
import ListContainer from "./ListContainer";

import { getEttersendelserTilGodkjenning as getEttersendelserTilGodkjenningAction } from "../../../actions/ettersendelserTilGodkjenningActions";
import { getAllIleggelseEttersendelserbreverPdf } from "../../../actions/ileggelseEttersendelsebrevActions";
import { putIleggelseEttersendelser as putIleggelseEttersendelserActions } from "../../../actions/ileggelseEttersendelserActions";
import { getSingleMotorvognByQuery as getSingleMotorvognByQueryAction } from "../../../actions/motorvognActions";
import { getEttersendelserTilGodkjenningList } from "../../../selectors/ettersendelserTilGodkjenningSelector";

import * as title from "../../../constants/titles";

import "./EttersendelserTilGodkjenningPage.css";

const PaginationControl = createPaginationControl(
  "ettersendelserTilGodkjenning",
  {
    apiCallAction: getEttersendelserTilGodkjenningAction,
    queriesForApiReducerName: "forms.ettersendelserTilGodkjenningFilterForm",
  },
  getEttersendelserTilGodkjenningList,
);

function buildCheckedEttersendelserTilGodkjenning(items) {
  return {
    ..._.reduce(
      items.map((e) => ({
        [e.ettersendelse && e.ettersendelse.id]: false,
      })),
      _.extend,
    ),
    checkboxAllEttersendelserTilGodkjenning: false,
  };
}

export default function EttersendelserTilGodkjenningContainer() {
  const dispatch = useDispatch();
  const ettersendelserTilGodkjenning = useSelector(
    (state) => state.ettersendelserTilGodkjenning,
  );

  const [showGodkjennOgSkrivUtSpinner, setShowGodkjennOgSkrivUtSpinner] =
    useState(false);
  const [
    waitingForGetEttersendelserTilGodkjenning,
    setWaitingForGetEttersendelserTilGodkjenning,
  ] = useState(false);
  const [
    checkedEttersendelserTilGodkjenning,
    setCheckedEttersendelserTilGodkjenning,
  ] = useState(() =>
    buildCheckedEttersendelserTilGodkjenning(
      ettersendelserTilGodkjenning.items,
    ),
  );
  const [max] = useState(10);

  const getEttersendelserTilGodkjenningFilter = () => ({
    isEttersendelseTilGodkjenning: true,
    isMakulert: false,
    page: ettersendelserTilGodkjenning.page,
    pageSize: ettersendelserTilGodkjenning.count,
    include: "ettersendelse",
  });

  const putIleggelseEttersendelserBatchJob = (
    putIleggelseEttersendelserDataArray,
  ) =>
    Promise.all(
      putIleggelseEttersendelserDataArray.map((pied) =>
        dispatch(
          putIleggelseEttersendelserActions(pied.ettersendelseId, pied.data),
        ),
      ),
    )
      .then(() => Promise.resolve)
      .catch(() => Promise.reject);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = title.ileggelse.ettersendelserTilGodkjenning;
    if (
      ettersendelserTilGodkjenning.page &&
      ettersendelserTilGodkjenning.count
    ) {
      setWaitingForGetEttersendelserTilGodkjenning(true);
      dispatch(
        getEttersendelserTilGodkjenningAction(
          getEttersendelserTilGodkjenningFilter(),
        ),
      )
        .then((etgList) => {
          setWaitingForGetEttersendelserTilGodkjenning(false);
          const etgMissingMotorvognInfo = etgList.filter(
            (etg) =>
              etg.ettersendelse &&
              (!etg.ettersendelse.navn ||
                !etg.ettersendelse.gate ||
                !etg.ettersendelse.postnummer ||
                !etg.ettersendelse.poststed),
          );
          Promise.all(
            etgMissingMotorvognInfo.map((etgmmi) => {
              if (etgmmi.nasjon === "Norge") {
                return dispatch(
                  getSingleMotorvognByQueryAction({
                    kjennemerke: etgmmi.kjennemerke,
                    dato: etgmmi.ileggelsesdatoTil,
                  }),
                );
              }
              return null;
            }),
          )
            .then((responseArray) => {
              const putDataArray = [];
              for (let i = 0; i < responseArray.length; i++) {
                const motorvogn =
                  responseArray[i] && responseArray[i].data.result;
                if (!motorvogn || !motorvogn.kjennemerke) continue;
                const matchingEtg = etgMissingMotorvognInfo.filter(
                  (etg) =>
                    etg.kjennemerke.replace(" ", "") ===
                    motorvogn.kjennemerke.replace(" ", ""),
                );
                if (matchingEtg.length < 1) continue;
                for (let j = 0; j < matchingEtg.length; j++) {
                  const etg = matchingEtg[j];
                  const data = {
                    navn:
                      etg.ettersendelse.navn ||
                      motorvogn.eierOrgNavn ||
                      motorvogn.eierPersonNavn,
                    gate: etg.ettersendelse.gate || motorvogn.eierAdresse,
                    postnummer:
                      etg.ettersendelse.postnummer || motorvogn.eierPostNr,
                    poststed:
                      etg.ettersendelse.poststed || motorvogn.eierPostSted,
                  };
                  putDataArray.push({
                    ettersendelseId: etg.ettersendelse.id,
                    data,
                  });
                }
              }
              if (etgMissingMotorvognInfo.length > 0) {
                putIleggelseEttersendelserBatchJob(putDataArray)
                  .then(() =>
                    dispatch(
                      getEttersendelserTilGodkjenningAction(
                        getEttersendelserTilGodkjenningFilter(),
                      ),
                    ),
                  )
                  .catch();
              }
            })
            .catch();
        })
        .catch(() => setWaitingForGetEttersendelserTilGodkjenning(false));
    }
  }, []);

  // Replaces UNSAFE_componentWillReceiveProps
  useEffect(() => {
    setCheckedEttersendelserTilGodkjenning(
      buildCheckedEttersendelserTilGodkjenning(
        ettersendelserTilGodkjenning.items,
      ),
    );
  }, [ettersendelserTilGodkjenning.items]);

  const handleCheckboxChange = (event) => {
    if (event.target.name === "checkboxAllEttersendelserTilGodkjenning") {
      const keys = Object.keys(checkedEttersendelserTilGodkjenning);
      setCheckedEttersendelserTilGodkjenning(
        _.reduce(
          keys.map((k) => ({ [k]: event.target.checked })),
          _.extend,
        ),
      );
    } else {
      setCheckedEttersendelserTilGodkjenning({
        ...checkedEttersendelserTilGodkjenning,
        [event.target.name]: event.target.checked,
      });
    }
  };

  const checkedEttersendelserArray = () => {
    const keys = Object.keys(checkedEttersendelserTilGodkjenning);
    return keys.filter(
      (k) =>
        k !== "checkboxAllEttersendelserTilGodkjenning" &&
        checkedEttersendelserTilGodkjenning[k],
    );
  };

  const postIleggelseEttersendelsesbrevBatchJob = () => {
    const checkedArray = checkedEttersendelserArray();
    const slicedArray = checkedArray.slice(0, max);
    setShowGodkjennOgSkrivUtSpinner(true);
    dispatch(
      getAllIleggelseEttersendelserbreverPdf({
        ettersendelseId: slicedArray,
      }),
    )
      .then(() => {
        setShowGodkjennOgSkrivUtSpinner(false);
        dispatch(
          getEttersendelserTilGodkjenningAction(
            getEttersendelserTilGodkjenningFilter(),
          ),
        );
      })
      .catch(() => setShowGodkjennOgSkrivUtSpinner(false));
  };

  const showGodkjennSkrivUtButton = () => {
    const checkedKeys = Object.keys(checkedEttersendelserTilGodkjenning).filter(
      (key) => checkedEttersendelserTilGodkjenning[key],
    );
    const checkedItems = ettersendelserTilGodkjenning.items.filter((e) =>
      checkedKeys.some((ck) => ck === e.ettersendelse.id),
    );
    return (
      checkedItems.length !== 0 &&
      checkedItems.every(
        (e) =>
          e.isEttersendelseTilGodkjenning &&
          !e.ileggelseisMakulert &&
          !e.isBetalingsfristUtlopt &&
          !e.isBetalt &&
          !e.isVenter,
      )
    );
  };

  const array = checkedEttersendelserArray();
  const size = array.length;

  return (
    <>
      <NavigationBarContainer page="ettersendelser-til-godkjenning" />
      {(!waitingForGetEttersendelserTilGodkjenning ||
        ettersendelserTilGodkjenning.items.length !== 0) && (
        <React.Fragment>
          {showGodkjennSkrivUtButton() && (
            <div
              className="pull-left"
              style={{
                paddingTop: 12,
                paddingBottom: 12,
                paddingLeft: 50,
              }}
            >
              <LoadButton
                isLoading={showGodkjennOgSkrivUtSpinner}
                buttonProps={{
                  className: "btn btn-bym-standard btn-bym-medium",
                  onClick: postIleggelseEttersendelsesbrevBatchJob,
                  disabled: showGodkjennOgSkrivUtSpinner,
                }}
                spinnerProps={{ pColor: "#ffffff", sColor: "#4d4d4d" }}
              >
                Send til Svar ut
              </LoadButton>
              <div className="red" style={{ marginTop: 10 }}>
                {size > max
                  ? `Antall brev pr. kjøring er begrenset til ${max}`
                  : ""}
              </div>
            </div>
          )}
          <div
            className="pull-right ileggelse-list-page-total-ileggelser"
            style={{
              paddingTop: 12,
              paddingBottom: 12,
              paddingRight: 50,
            }}
          >
            {ettersendelserTilGodkjenning.total} ileggelser
          </div>
          <div className="clearfix" />
          <div className="ileggelse-list-page-content-container">
            <ListContainer
              checkedEttersendelserTilGodkjenning={
                checkedEttersendelserTilGodkjenning
              }
              handleCheckboxChange={handleCheckboxChange}
              getEttersendelserTilGodkjenningFilter={
                getEttersendelserTilGodkjenningFilter
              }
            />
          </div>
          <PaginationControl storeKey="ettersendelserTilGodkjenning" />
        </React.Fragment>
      )}
      <Spinner
        isLoading={
          waitingForGetEttersendelserTilGodkjenning &&
          ettersendelserTilGodkjenning.items.length === 0
        }
      />
      <div style={{ height: 50 }} />
    </>
  );
}
